import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiEye, FiEyeOff } from "react-icons/fi";

/* ========== THEME ========== */
const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  accent: "#ff914d",
  text: "#f1f1f1",
  subtext: "#d6d9de",
  bg: "#0b1320",
  card: "#0f1b2d",
  border: "#1e293b",
};

/* ========== ANIMATIONS ========== */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ========== STYLED COMPONENTS ========== */
const Page = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: radial-gradient(circle at 10% 10%, #10233c 0%, #091120 50%, #060b14 100%);
  color: ${theme.text};
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
`;

const Card = styled.div`
  background: ${theme.card};
  padding: 40px 32px;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  width: 100%;
  max-width: 400px;
  text-align: center;
  animation: ${fadeIn} 0.6s ease forwards;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const Logo = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${theme.brand}, ${theme.brandDark});
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 20px;
  color: ${theme.text};
`;

const BrandName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${theme.text};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 48px 12px 16px;
  border-radius: 12px;
  border: 1px solid ${theme.border};
  background: ${theme.bg};
  color: ${theme.text};
  font-size: 14px;
  outline: none;
  transition: border 0.2s ease;

  &:focus {
    border: 1px solid ${theme.accent};
  }

  &::placeholder {
    color: ${theme.subtext};
  }
`;

const ToggleIcon = styled.div`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  cursor: pointer;
  color: ${theme.subtext};
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.accent};
  }
`;

const Button = styled.button`
  padding: 12px 16px;
  border-radius: 12px;
  border: none;
  background: linear-gradient(135deg, ${theme.brand}, ${theme.brandDark});
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  opacity: ${(p) => (p.disabled ? 0.7 : 1)};
  pointer-events: ${(p) => (p.disabled ? "none" : "auto")};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
  }
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: #ff4d4f;
  margin: 0;
  text-align: left;
`;

const FooterText = styled.p`
  font-size: 12px;
  color: ${theme.subtext};
  margin-top: 12px;
`;

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;


/* ========== COMPONENT ========== */
export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          method: "GET",
          credentials: "include",
        });

        // Only trust JSON (avoid HTML 200 from dev server)
        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) return;

        const data = await res.json().catch(() => null);
        if (!ignore && res.ok && data && data.admin) {
          navigate("/admin/dashboard", { replace: true });
        }
      } catch {
        // ignore network issues
      }
    })();
    return () => {
      ignore = true;
    };
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (res.ok) {
        setLoading(false);
        navigate("/admin/dashboard");
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (res.status === 422 && data?.errors?.length) {
        setError(data.errors.map((e) => e.msg).join(" • "));
      } else if (res.status === 401) {
        setError(data?.message || "Invalid email or password");
      } else {
        setError(data?.message || "Something went wrong. Please try again.");
      }
      setLoading(false);
    } catch {
      setLoading(false);
      setError("Network error. Check your connection/server.");
    }
  };

  return (
    <Page>
      <Card>
        <Brand>
          <Logo>AI</Logo>
          <BrandName>Building Skills</BrandName>
        </Brand>

        <Form onSubmit={handleSubmit}>
          <Input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />

          <InputWrapper>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <ToggleIcon
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label="toggle password visibility"
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </ToggleIcon>
          </InputWrapper>

          {error && <ErrorText>{error}</ErrorText>}

          <Button type="submit" disabled={loading}>
            {loading ? "Signing in..." : "Login"}
          </Button>
        </Form>

        <FooterText>© 2025 AI Building Skills. All rights reserved.</FooterText>
      </Card>
    </Page>
  );
}
